import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "baseSelect",
    "baseSection",
    "joinToggle",
    "joinFields",
    "activeDrop",
    "activeTables",
    "activeHint",
    "joinDrop",
    "joinTables",
    "joinHint",
    "joinStatus",
    "aggregationGroupBy",
    "groupBySelect",
    "groupByList",
    "groupByEmpty",
    "aggregationMetric",
    "preview",
    "previewSql",
    "previewDebug",
    "previewButton",
    "joinMapSource",
    "tableLabelsSource",
    "fieldMapSource",
    "filterList",
    "filterTemplate",
    "filterMatch",
    "metricFunction",
    "metricFilters",
    "metricSecondaryFunction",
    "metricSecondaryFilters",
    "metricSection",
    "hiddenFilters",
  ]

  static values = {
    joinMap: String,
    tableLabels: String,
    fieldMap: String,
    filterConfig: String,
    previewUrl: String,
    baseTable: String,
    aggregationConfig: String,
  }

  connect() {
    this._joinMap = this.parseValue(this.sourceValue("joinMapSource", this.joinMapValue))
    this._tableLabels = this.parseValue(this.sourceValue("tableLabelsSource", this.tableLabelsValue))
    this._fieldMap = this.parseValue(this.sourceValue("fieldMapSource", this.fieldMapValue))
    this._filterConfig = this.parseValue(this.filterConfigValue)
    this._aggregationConfig = this.parseValue(this.aggregationConfigValue)
    this.normalizeFilterConfig()
    if (this.hasBaseSelectTarget && !this.baseSelectTarget.value && this.baseTableValue) {
      this.baseSelectTarget.value = this.baseTableValue
    }
    this._boundHandleSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener("submit", this._boundHandleSubmit)
    this.refreshUI()
    this.updateMetricFilterVisibility()
    this.syncMetricSections()
  }

  disconnect() {
    if (this._boundHandleSubmit) {
      this.element.removeEventListener("submit", this._boundHandleSubmit)
    }
  }

  dragStart(event) {
    const tableKey = event.currentTarget.dataset.tableKey
    if (!tableKey) return
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", tableKey)
  }

  dragOver(event) {
    event.preventDefault()
  }

  dropBase(event) {
    event.preventDefault()
    const tableKey = event.dataTransfer.getData("text/plain")
    if (!tableKey) return
    if (this.baseValue() === tableKey) return

    this.baseSelectTarget.value = tableKey
    this.clearSelections()
    this.refreshUI()
  }

  dropJoin(event) {
    event.preventDefault()
    const tableKey = event.dataTransfer.getData("text/plain")
    if (!tableKey) return

    const base = this.baseValue()
    if (!base) {
      this.setJoinStatus("Select a base table before adding joins.")
      return
    }

    const baseJoins = this.joinMap()[base] || []
    const tableLabels = this.tableLabels()
    const baseLabel = tableLabels[base] || base
    const allowed = baseJoins.find(
      (join) =>
        join.name === tableKey ||
        join.table === tableKey ||
        join.label === tableKey ||
        (tableLabels[join.table] || join.table) === tableKey
    )
    if (!allowed) {
      const joinList = baseJoins.map((join) => join.label).filter(Boolean)
      const hint =
        joinList.length > 0
          ? `Valid joins from ${baseLabel}: ${joinList.join(", ")}.`
          : `No joins are configured for ${baseLabel}.`
      this.setJoinStatus(`That table cannot be joined to the selected base table. ${hint}`)
      return
    }

    const toggle = this.joinToggleTargets.find(
      (checkbox) =>
        checkbox.dataset.baseTable === base && checkbox.dataset.joinName === allowed.name
    )
    if (toggle) toggle.checked = true
    this.setJoinStatus(`${allowed.label} join added.`)
    this.refreshUI()
  }

  toggleJoin() {
    this.refreshUI()
  }

  baseChanged() {
    this.clearSelections()
    this.refreshUI()
  }

  preview(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (!this.previewUrlValue) {
      if (this.hasPreviewTarget) {
        this.previewTarget.innerHTML =
          "<div class=\"alert alert-warning\">Preview URL is missing. Please refresh the page.</div>"
      }
      if (this.hasPreviewSqlTarget) {
        this.previewSqlTarget.textContent = ""
      }
      return
    }
    const form = this.element.closest("form") || this.element
    this.prepareFilterHiddenInputs(form)
    const formData = new FormData(form)

    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML =
        "<div class=\"alert alert-info\">Generating preview…</div>"
    }
    if (this.hasPreviewSqlTarget) {
      this.previewSqlTarget.textContent = ""
    }
    if (this.hasPreviewDebugTarget) {
      this.previewDebugTarget.textContent = ""
    }

    fetch(this.previewUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken(),
      },
      body: formData,
    })
      .then(async (response) => {
        const payload = await response.json()
        if (this.hasPreviewTarget) {
          this.previewTarget.innerHTML = payload.html || ""
        }
        if (this.hasPreviewSqlTarget) {
          this.previewSqlTarget.textContent = payload.sql || ""
        }
        if (this.hasPreviewDebugTarget) {
          this.previewDebugTarget.textContent = payload.debug || ""
        }
      })
      .catch(() => {
        if (this.hasPreviewTarget) {
          this.previewTarget.innerHTML =
            "<div class=\"alert alert-danger\">Unable to generate preview.</div>"
        }
        if (this.hasPreviewSqlTarget) {
          this.previewSqlTarget.textContent = ""
        }
        if (this.hasPreviewDebugTarget) {
          this.previewDebugTarget.textContent = ""
        }
      })
  }

  handleSubmit() {
    const form = this.element.closest("form") || this.element
    this.prepareFilterHiddenInputs(form)
  }

  refreshUI() {
    this.renderActiveTables()
    this.applyBaseVisibility()
    this.applyJoinVisibility()
    this.renderAggregationOptions()
    this.applyAggregationConfig()
    this.updateSelectedFieldEmptyStates()
    this.updateGroupByEmptyStates()
    this.renderFilters()
    this.updateMetricFilterVisibility()
    this.syncMetricSections()
  }

  toggleMetricFilters() {
    this.updateMetricFilterVisibility()
  }

  updateMetricFilterVisibility() {
    const showPrimary = this.metricFunctionTargets.some((select) => select.value)
    this.metricFiltersTargets.forEach((section) => {
      section.classList.toggle("d-none", !showPrimary)
    })

    const showSecondary = this.metricSecondaryFunctionTargets.some((select) => select.value)
    this.metricSecondaryFiltersTargets.forEach((section) => {
      section.classList.toggle("d-none", !showSecondary)
    })
  }

  addMetric() {
    const hiddenSections = this.metricSectionTargets.filter((section) =>
      section.classList.contains("d-none")
    )
    const nextSection = hiddenSections.sort(this.metricSectionSort())[0]
    if (nextSection) {
      nextSection.classList.remove("d-none")
    }
  }

  removeMetric(event) {
    const section = event.currentTarget.closest("[data-report-builder-target='metricSection']")
    if (!section) return
    section.querySelectorAll("select, input").forEach((input) => {
      input.value = ""
    })
    section.classList.add("d-none")
    this.updateMetricFilterVisibility()
  }

  syncMetricSections() {
    if (!this.hasMetricSectionTarget) return
    this.metricSectionTargets.forEach((section) => {
      const hasValue = Array.from(section.querySelectorAll("select, input")).some(
        (input) => input.value && input.value.toString().trim() !== ""
      )
      section.classList.toggle("d-none", !hasValue)
    })
  }

  metricSectionSort() {
    return (a, b) => {
      const aIndex = Number.parseInt(a.dataset.metricIndex || "0", 10)
      const bIndex = Number.parseInt(b.dataset.metricIndex || "0", 10)
      return aIndex - bIndex
    }
  }

  renderActiveTables() {
    const base = this.baseValue()
    this.activeTablesTarget.innerHTML = ""
    this.joinTablesTarget.innerHTML = ""

    if (!base) {
      this.activeHintTarget.classList.remove("d-none")
      this.joinHintTarget.classList.remove("d-none")
      return
    }

    this.activeHintTarget.classList.add("d-none")
    const baseLabel = this.tableLabels()[base] || base
    this.activeTablesTarget.appendChild(this.buildPill(baseLabel, "Base"))

    const joins = this.selectedJoins()
    if (joins.length === 0) {
      this.joinHintTarget.classList.remove("d-none")
    } else {
      this.joinHintTarget.classList.add("d-none")
      joins.forEach((joinName) => {
        const label = this.joinLabel(base, joinName)
        this.joinTablesTarget.appendChild(this.buildPill(label, "Join", "removeJoin", joinName))
      })
    }
  }

  buildPill(label, badgeText, actionName, actionValue) {
    const wrapper = document.createElement("div")
    wrapper.className = "report-builder-pill active"
    if (actionName) {
      wrapper.dataset.action = `click->report-builder#${actionName}`
      wrapper.dataset.value = actionValue
    }

    const span = document.createElement("span")
    span.textContent = label
    wrapper.appendChild(span)

    const badge = document.createElement("span")
    badge.className = "report-builder-pill-badge"
    badge.textContent = badgeText
    wrapper.appendChild(badge)

    return wrapper
  }

  applyBaseVisibility() {
    const base = this.baseValue()
    this.baseSectionTargets.forEach((section) => {
      section.classList.toggle("d-none", section.dataset.baseTable !== base)
    })
  }

  applyJoinVisibility() {
    const base = this.baseValue()
    this.joinFieldsTargets.forEach((section) => {
      const matchesBase = section.dataset.baseTable === base
      const joinName = section.dataset.joinFields
      const toggle = this.joinToggleTargets.find(
        (checkbox) =>
          checkbox.dataset.baseTable === base && checkbox.dataset.joinName === joinName
      )
      const show = matchesBase && toggle && toggle.checked
      section.classList.toggle("d-none", !show)
    })
  }

  renderAggregationOptions() {
    if (!this.hasAggregationGroupByTarget || !this.hasAggregationMetricTarget) return
    const base = this.baseValue()
    if (!base) {
      this.aggregationGroupByTarget.innerHTML = ""
      this.aggregationMetricTargets.forEach((target) => {
        target.innerHTML = ""
      })
      return
    }

    const joinNames = this.selectedJoins()
    const fieldMap = this.fieldMap()[base] || { base: [], joins: {} }

    const fields = [
      ...fieldMap.base.map((field) => ({ ...field, group: this.tableLabels()[base] })),
      ...joinNames.flatMap((joinName) => {
        const joinFields = fieldMap.joins[joinName] || []
        const label = this.joinLabel(base, joinName)
        return joinFields.map((field) => ({ ...field, group: label }))
      }),
    ]

    this.aggregationGroupByTarget.innerHTML = ""
    this.aggregationMetricTargets.forEach((target) => {
      target.innerHTML = ""
    })

    this.aggregationMetricTargets.forEach((target) => {
      const blank = document.createElement("option")
      blank.value = ""
      blank.textContent = "Select field"
      target.appendChild(blank)
    })

    fields.forEach((field) => {
      const option = document.createElement("option")
      option.value = field.id
      option.textContent = `${field.group} • ${field.label}`
      option.dataset.label = field.label
      this.aggregationGroupByTarget.appendChild(option)

      this.aggregationMetricTargets.forEach((target) => {
        const metricOption = document.createElement("option")
        metricOption.value = field.id
        metricOption.textContent = `${field.group} • ${field.label}`
        target.appendChild(metricOption)
      })
    })
  }

  applyAggregationConfig() {
    const config = this._aggregationConfig || {}
    const hasAnyMetricValue = [
      config.metric_secondary,
      config.metric_tertiary,
      config.metric_quaternary,
      config.function_secondary,
      config.function_tertiary,
      config.function_quaternary,
    ].some((value) => value && value.toString().trim() !== "")
    if (this.hasAggregationGroupByTarget) {
      const groupBy = Array.isArray(config.group_by) ? config.group_by : []
      Array.from(this.aggregationGroupByTarget.options).forEach((option) => {
        option.selected = groupBy.includes(option.value)
      })
      this.ensureGroupBySelection(groupBy, config.group_by_labels || {})
    }
    this.setSelectValue("report[aggregation_config][metric]", config.metric)
    this.setSelectValue("report[aggregation_config][function]", config.function)
    this.setInputValue("report[aggregation_config][metric_label]", config.metric_label)
    this.setSelectValue(
      "report[aggregation_config][metric_filter_operator]",
      config.metric_filter_operator
    )
    this.setInputValue("report[aggregation_config][metric_filter_value]", config.metric_filter_value)
    this.setInputValue(
      "report[aggregation_config][metric_filter_value_to]",
      config.metric_filter_value_to
    )

    this.setSelectValue("report[aggregation_config][metric_secondary]", config.metric_secondary)
    this.setSelectValue("report[aggregation_config][function_secondary]", config.function_secondary)
    this.setInputValue(
      "report[aggregation_config][metric_secondary_label]",
      config.metric_secondary_label
    )
    this.setSelectValue(
      "report[aggregation_config][metric_secondary_filter_operator]",
      config.metric_secondary_filter_operator
    )
    this.setInputValue(
      "report[aggregation_config][metric_secondary_filter_value]",
      config.metric_secondary_filter_value
    )
    this.setInputValue(
      "report[aggregation_config][metric_secondary_filter_value_to]",
      config.metric_secondary_filter_value_to
    )

    this.setSelectValue("report[aggregation_config][metric_tertiary]", config.metric_tertiary)
    this.setSelectValue("report[aggregation_config][function_tertiary]", config.function_tertiary)
    this.setInputValue(
      "report[aggregation_config][metric_tertiary_label]",
      config.metric_tertiary_label
    )

    this.setSelectValue("report[aggregation_config][metric_quaternary]", config.metric_quaternary)
    this.setSelectValue(
      "report[aggregation_config][function_quaternary]",
      config.function_quaternary
    )
    this.setInputValue(
      "report[aggregation_config][metric_quaternary_label]",
      config.metric_quaternary_label
    )

    if (!hasAnyMetricValue) {
      this.metricSectionTargets.forEach((section) => {
        section.classList.add("d-none")
      })
    }
  }

  addGroupByField(event) {
    event.preventDefault()
    if (!this.hasGroupBySelectTarget || !this.hasGroupByListTarget) return
    const fieldId = this.groupBySelectTarget.value
    if (!fieldId) return
    if (this.groupByListTarget.querySelector(`[data-field-id="${CSS.escape(fieldId)}"]`)) {
      this.groupBySelectTarget.value = ""
      return
    }
    const selectedOption = this.groupBySelectTarget.selectedOptions[0]
    const label =
      selectedOption?.textContent?.trim() || selectedOption?.dataset.label?.trim() || fieldId
    this.appendGroupByField(fieldId, label, "")
    this.groupBySelectTarget.value = ""
  }

  removeGroupByField(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-report-builder-group-by-field]")
    if (!row) return
    row.remove()
    this.updateGroupByEmptyStates()
  }

  appendGroupByField(fieldId, label, headerValue) {
    if (!this.hasGroupByListTarget) return
    const wrapper = document.createElement("div")
    wrapper.className = "report-builder-selected-field border rounded-3 p-2 mb-2"
    wrapper.dataset.reportBuilderGroupByField = "true"
    wrapper.dataset.fieldId = fieldId
    wrapper.innerHTML = `
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div class="fw-semibold">${label}</div>
        <button type="button" class="btn btn-outline-danger btn-sm" data-action="report-builder#removeGroupByField">Remove</button>
      </div>
      <div class="row g-2 mt-2">
        <div class="col-12 col-md-6">
          <label class="form-label">Column header</label>
          <input class="form-control form-control-sm" name="report[aggregation_config][group_by_labels][${fieldId}]" type="text" placeholder="Optional label" value="${headerValue || ""}" />
        </div>
      </div>
      <input type="hidden" name="report[aggregation_config][group_by][]" value="${fieldId}">
    `
    this.groupByListTarget.appendChild(wrapper)
    this.updateGroupByEmptyStates()
  }

  ensureGroupBySelection(groupBy, labels) {
    if (!this.hasGroupByListTarget || !this.hasAggregationGroupByTarget) return
    if (this.groupByListTarget.querySelectorAll("[data-report-builder-group-by-field]").length > 0) {
      return
    }
    this.groupByListTarget.innerHTML = ""
    groupBy.forEach((fieldId) => {
      const option = Array.from(this.aggregationGroupByTarget.options).find(
        (opt) => opt.value === fieldId
      )
      const label = option?.textContent?.trim() || fieldId
      const headerValue = labels?.[fieldId] || ""
      this.appendGroupByField(fieldId, label, headerValue)
    })
    this.updateGroupByEmptyStates()
  }

  updateGroupByEmptyStates() {
    if (!this.hasGroupByEmptyTarget || !this.hasGroupByListTarget) return
    const hasItems =
      this.groupByListTarget.querySelectorAll("[data-report-builder-group-by-field]").length > 0
    this.groupByEmptyTarget.classList.toggle("d-none", hasItems)
  }

  renderFilters() {
    if (!this.hasFilterListTarget || !this.hasFilterTemplateTarget) return
    const matchValue = this._filterConfig?.match || "all"
    if (this.hasFilterMatchTarget) {
      this.filterMatchTarget.value = matchValue
    }

    if (this.filterListTarget.children.length === 0) {
      const rules = this.normalizedFilterRules()
      if (rules.length === 0) {
        return
      }
      rules.forEach((rule) => this.addFilter(rule))
    } else {
      this.updateFilterFieldOptions()
    }
  }

  normalizeFilterConfig() {
    if (!this._filterConfig || typeof this._filterConfig !== "object") return
    if (Array.isArray(this._filterConfig.rules) && this._filterConfig.rules.length > 0) return
    const rulesJson = this._filterConfig.rules_json
    if (!rulesJson) return
    try {
      const parsed = JSON.parse(rulesJson)
      if (Array.isArray(parsed)) {
        this._filterConfig.rules = parsed
      }
    } catch (_error) {
      // ignore malformed JSON
    }
  }

  addFilter(rule = {}) {
    if (!this.hasFilterTemplateTarget || !this.hasFilterListTarget) return
    const index = `${Date.now()}-${Math.floor(Math.random() * 1000)}`
    const template = this.filterTemplateTarget.innerHTML.replace(/__INDEX__/g, index)
    const wrapper = document.createElement("div")
    wrapper.innerHTML = template
    const row = wrapper.firstElementChild
    this.filterListTarget.appendChild(row)

    const fieldSelect = row.querySelector("[data-report-builder-filter-field]")
    const operatorSelect = row.querySelector("[data-report-builder-filter-operator]")
    const valueInput = row.querySelector("[data-report-builder-filter-value]")
    const valueToInput = row.querySelector("[data-report-builder-filter-value-to]")

    if (fieldSelect) {
      this.populateFilterFieldOptions(fieldSelect)
      if (rule.field) fieldSelect.value = rule.field
    }
    if (operatorSelect && rule.operator) operatorSelect.value = rule.operator
    if (valueInput && rule.value !== undefined && rule.value !== null) {
      valueInput.value = rule.value
    }
    if (valueToInput && rule.value_to !== undefined && rule.value_to !== null) {
      valueToInput.value = rule.value_to
    }

    this.updateFilterOperator({ target: operatorSelect })
  }

  removeFilter(event) {
    const row = event.currentTarget.closest(".report-builder-filter-row")
    if (row) row.remove()
  }

  updateFilterOperator(event) {
    const select = event?.target
    if (!select) return
    const row = select.closest(".report-builder-filter-row")
    if (!row) return
    const wrapper = row.querySelector("[data-report-builder-filter-value-to-wrapper]")
    if (!wrapper) return
    const showBetween = select.value === "between"
    wrapper.classList.toggle("d-none", !showBetween)
  }

  updateFilterFieldOptions() {
    if (!this.hasFilterListTarget) return
    this.filterListTarget.querySelectorAll("[data-report-builder-filter-field]").forEach((select) => {
      const currentValue = select.value
      this.populateFilterFieldOptions(select)
      if (currentValue) {
        select.value = currentValue
      }
    })
  }

  populateFilterFieldOptions(select) {
    const fields = this.availableFilterFields()
    select.innerHTML = ""
    const placeholder = document.createElement("option")
    placeholder.value = ""
    placeholder.textContent = "Select field"
    select.appendChild(placeholder)

    fields.forEach((field) => {
      const option = document.createElement("option")
      option.value = field.id
      option.textContent = `${field.group} • ${field.label}`
      select.appendChild(option)
    })
  }

  availableFilterFields() {
    const base = this.baseValue()
    if (!base) return []
    const joinNames = this.selectedJoins()
    const fieldMap = this.fieldMap()[base] || { base: [], joins: {} }
    const baseLabel = this.tableLabels()[base] || base

    const fields = fieldMap.base.map((field) => ({
      id: field.id,
      label: field.label,
      group: baseLabel,
    }))

    joinNames.forEach((joinName) => {
      const joinFields = fieldMap.joins[joinName] || []
      const joinLabel = this.joinLabel(base, joinName)
      joinFields.forEach((field) => {
        fields.push({
          id: field.id,
          label: field.label,
          group: joinLabel,
        })
      })
    })

    return fields
  }

  normalizedFilterRules() {
    const rules = this._filterConfig?.rules
    if (Array.isArray(rules)) return rules
    if (rules && typeof rules === "object") {
      return Object.values(rules)
    }
    return []
  }

  collectFilterRules() {
    if (!this.hasFilterListTarget) return []
    const rows = Array.from(this.filterListTarget.querySelectorAll(".report-builder-filter-row"))
    const rules = rows
      .map((row) => {
        const field = row.querySelector("[data-report-builder-filter-field]")?.value || ""
        const operator = row.querySelector("[data-report-builder-filter-operator]")?.value || ""
        const value = row.querySelector("[data-report-builder-filter-value]")?.value || ""
        const valueTo = row.querySelector("[data-report-builder-filter-value-to]")?.value || ""
        return { field, operator, value, value_to: valueTo }
      })
      .filter((rule) => rule.field || rule.operator || rule.value || rule.value_to)

    if (rules.length > 0) return rules
    return this.collectFilterRulesFromInputs()
  }

  collectFilterRulesFromInputs() {
    if (!this.hasFilterListTarget) return []
    const inputs = Array.from(
      this.filterListTarget.querySelectorAll(
        "select[name^='report[filter_config][rules]'], input[name^='report[filter_config][rules]']"
      )
    )
    if (inputs.length === 0) return []
    const rulesByIndex = {}
    inputs.forEach((input) => {
      const name = input.getAttribute("name") || ""
      const match = name.match(/report\\[filter_config\\]\\[rules\\]\\[(.+?)\\]\\[(field|operator|value|value_to)\\]/)
      if (!match) return
      const index = match[1]
      const key = match[2]
      rulesByIndex[index] ||= { field: "", operator: "", value: "", value_to: "" }
      rulesByIndex[index][key] = input.value || ""
    })
    return Object.values(rulesByIndex).filter(
      (rule) => rule.field || rule.operator || rule.value || rule.value_to
    )
  }

  prepareFilterHiddenInputs(form) {
    if (!form) return
    const existing = form.querySelectorAll("[data-report-builder-generated='true']")
    existing.forEach((node) => node.remove())
    const rules = this.collectFilterRules()
    if (this.hasHiddenFiltersTarget) {
      this.hiddenFiltersTarget.value = rules.length > 0 ? JSON.stringify(rules) : ""
    }
    if (rules.length === 0) return

    rules.forEach((rule, index) => {
      this.appendHiddenInput(form, `report[filter_config][rules][${index}][field]`, rule.field)
      this.appendHiddenInput(form, `report[filter_config][rules][${index}][operator]`, rule.operator)
      this.appendHiddenInput(form, `report[filter_config][rules][${index}][value]`, rule.value)
      if (rule.value_to) {
        this.appendHiddenInput(form, `report[filter_config][rules][${index}][value_to]`, rule.value_to)
      }
    })
  }

  appendHiddenInput(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value || ""
    input.dataset.reportBuilderGenerated = "true"
    form.appendChild(input)
  }

  addSelectedField(event) {
    event.preventDefault()
    const scope = event.currentTarget.closest("[data-report-builder-field-scope]")
    if (!scope) return
    const select = scope.querySelector("[data-report-builder-field-select]")
    const list = scope.querySelector("[data-report-builder-selected-fields]")
    const empty = scope.querySelector("[data-report-builder-empty]")
    if (!select || !list) return
    const fieldId = select.value
    if (!fieldId) return
    if (list.querySelector(`[data-field-id="${CSS.escape(fieldId)}"]`)) {
      select.value = ""
      return
    }
    const selectedOption = select.selectedOptions[0]
    const label =
      selectedOption?.dataset.label?.trim() ||
      selectedOption?.textContent?.trim() ||
      fieldId

    const wrapper = document.createElement("div")
    wrapper.className = "report-builder-selected-field border rounded-3 p-2 mb-2"
    wrapper.dataset.reportBuilderSelectedField = "true"
    wrapper.dataset.fieldId = fieldId
    wrapper.innerHTML = `
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div class="fw-semibold">${label}</div>
        <button type="button" class="btn btn-outline-danger btn-sm" data-action="report-builder#removeSelectedField">Remove</button>
      </div>
      <div class="row g-2 mt-2">
        <div class="col-12 col-md-6">
          <label class="form-label">Column header</label>
          <input class="form-control form-control-sm" name="report[selected_field_labels][${fieldId}]" type="text" placeholder="Optional label" />
        </div>
      </div>
      <input type="hidden" name="report[selected_fields][]" value="${fieldId}">
    `
    list.appendChild(wrapper)
    if (empty) empty.classList.add("d-none")
    select.value = ""
  }

  removeSelectedField(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-report-builder-selected-field]")
    if (!row) return
    const list = row.closest("[data-report-builder-selected-fields]")
    row.remove()
    if (list) {
      const empty = list.closest("[data-report-builder-field-scope]")?.querySelector(
        "[data-report-builder-empty]"
      )
      const hasItems = list.querySelectorAll("[data-report-builder-selected-field]").length > 0
      if (empty) empty.classList.toggle("d-none", hasItems)
    }
  }

  updateSelectedFieldEmptyStates() {
    this.element
      .querySelectorAll("[data-report-builder-field-scope]")
      .forEach((scope) => {
        const list = scope.querySelector("[data-report-builder-selected-fields]")
        const empty = scope.querySelector("[data-report-builder-empty]")
        if (!list || !empty) return
        const hasItems =
          list.querySelectorAll("[data-report-builder-selected-field]").length > 0
        empty.classList.toggle("d-none", hasItems)
      })
  }

  selectedJoins() {
    const base = this.baseValue()
    return this.joinToggleTargets
      .filter((checkbox) => checkbox.dataset.baseTable === base && checkbox.checked)
      .map((checkbox) => checkbox.dataset.joinName)
  }

  joinLabel(base, joinName) {
    const match = (this.joinMap()[base] || []).find((join) => join.name === joinName)
    return (
      match?.label ||
      this.tableLabels()[match?.table] ||
      this.tableLabels()[joinName] ||
      joinName
    )
  }

  setJoinStatus(message) {
    if (!this.hasJoinStatusTarget) return
    this.joinStatusTarget.textContent = message
  }

  removeJoin(event) {
    const joinName = event.currentTarget.dataset.value
    const base = this.baseValue()
    const toggle = this.joinToggleTargets.find(
      (checkbox) => checkbox.dataset.baseTable === base && checkbox.dataset.joinName === joinName
    )
    if (toggle) toggle.checked = false
    this.refreshUI()
  }

  clearSelections() {
    this.joinToggleTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
    this.element.querySelectorAll("[data-report-builder-selected-field]").forEach((node) => {
      node.remove()
    })
    if (this.hasGroupByListTarget) {
      this.groupByListTarget.innerHTML = ""
    }
    this.element
      .querySelectorAll("input[name='report[aggregation_config][group_by][]']")
      .forEach((node) => {
        node.remove()
      })
    this.element.querySelectorAll("input[name='report[selected_fields][]']").forEach((node) => {
      node.remove()
    })
    this.element.querySelectorAll("[data-report-builder-empty]").forEach((empty) => {
      empty.classList.remove("d-none")
    })
    this.updateGroupByEmptyStates()
  }

  baseValue() {
    return this.baseSelectTarget.value
  }

  joinMap() {
    return this._joinMap || {}
  }

  tableLabels() {
    return this._tableLabels || {}
  }

  fieldMap() {
    return this._fieldMap || {}
  }

  sourceValue(targetName, fallback) {
    const targetKey = `${targetName}Target`
    if (this.hasTarget(targetName)) {
      return this[targetKey].value
    }
    return fallback
  }

  hasTarget(targetName) {
    return this[`${targetName}Target`] !== undefined
  }

  parseValue(payload) {
    if (!payload) return {}
    if (typeof payload === "object") return payload
    try {
      const parsed = JSON.parse(payload)
      if (typeof parsed === "string") {
        return JSON.parse(parsed)
      }
      return parsed
    } catch (error) {
      try {
        const unescaped = payload
          .replaceAll("&quot;", "\"")
          .replaceAll("&#34;", "\"")
          .replaceAll("&amp;", "&")
        const parsed = JSON.parse(unescaped)
        if (typeof parsed === "string") {
          return JSON.parse(parsed)
        }
        return parsed
      } catch (_inner) {
        return {}
      }
    }
  }

  setSelectValue(name, value) {
    if (!value) return
    const select = this.element.querySelector(`select[name='${name}']`)
    if (select) {
      select.value = value
    }
  }

  setInputValue(name, value) {
    if (value === undefined || value === null) return
    const input = this.element.querySelector(`[name='${name}']`)
    if (input) {
      input.value = value
    }
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
